import { useState, useEffect, useRef } from "react";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { MdOutlineEmojiEmotions, MdPublic, MdLock, MdVisibilityOff } from "react-icons/md";
import { GoMention } from "react-icons/go";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaRobot } from "react-icons/fa";
import axios from "axios";
import { FaHashtag, FaRegClosedCaptioning } from "react-icons/fa";
import useLocalStorage from "use-local-storage";
import { useWorkspace } from '../../context/WorkspaceContext';
import { Modal } from "../Modal/Modal";
import AIChatModal from "./AIChatModal";

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

// Estimate line height for min-height calculation (adjust if needed)
const ESTIMATED_LINE_HEIGHT_PX = 20; 

const Textarea = ({ onDescriptionChange, initialDescription, placeholder, formData, handlePrivacyStatusChange, showThreeDots = true, showPrivacy = true, rows = 4, maxLength = null, hideBottomBar = false, onAIImageGenerated }) => {
    // 1. Enforce maxLength on initial state
    const getInitialValue = () => {
        const initial = initialDescription || '';
        return maxLength !== null && initial.length > maxLength ? initial.substring(0, maxLength) : initial;
    };
    const [description, setDescription] = useState(getInitialValue);
    const [isPickerVisible, setIsPickerVisible] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuItems, setMenuItems] = useState(null);
    const [activeWorkspace] = useLocalStorage("activeWorkspace");
    // Initialize count based on potentially truncated value
    const [wordCount, setWordCount] = useState(getInitialValue().length); 
    const menuContainerRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [visibility, setVisibility] = useState(formData.privacyStatus || 'public');
    const [isVisibilityDropdownOpen, setIsVisibilityDropdownOpen] = useState(false);
    const { idToken } = useWorkspace();
    const contentEditableRef = useRef(null); // Renamed ref

    // States for template modals
    const [isModalOpenHashtag, setIsModalOpenHashtag] = useState(false);
    const [isModalOpenCaption, setIsModalOpenCaption] = useState(false);
    
    // State for AI chat modal
    const [isAIChatModalOpen, setIsAIChatModalOpen] = useState(false);

    // Handle input directly from the contentEditable div
    const handleInput = (e) => {
        let value = e.currentTarget.textContent || '';

        // Enforce maxLength
        if (maxLength !== null && value.length > maxLength) {
            // Truncate the text and update the div content
            value = value.substring(0, maxLength);
            if(contentEditableRef.current) {
                contentEditableRef.current.textContent = value;
                // Attempt to restore cursor position to the end (may need refinement)
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(contentEditableRef.current);
                range.collapse(false); // false means collapse to end
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }

        // Update state and call prop
        setDescription(value);
        setWordCount(value.length);
        onDescriptionChange(value);
    };

    // Effect to set initial content and update when initialDescription prop changes
    // 2. Effect to handle EXTERNAL changes to initialDescription prop
    useEffect(() => {
        let incomingValue = initialDescription || '';
        // Enforce maxLength if the prop changes
        if (maxLength !== null && incomingValue.length > maxLength) {
            incomingValue = incomingValue.substring(0, maxLength);
        }

        // Only update if the (potentially truncated) incoming value is different from current state
        if (incomingValue !== description) {
            setDescription(incomingValue);
            setWordCount(incomingValue.length);
            // Also update the contentEditable div if it's different
            if (contentEditableRef.current && contentEditableRef.current.textContent !== incomingValue) {
                contentEditableRef.current.textContent = incomingValue;
            }
        }
        // Re-run only when initialDescription prop changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDescription, maxLength]); // Add maxLength as dependency


    // Effect to sync description state changes (e.g., from emoji, mentions, templates) back to the contentEditable div
    // This should receive already-validated lengths from internal handlers
    useEffect(() => {
        if (contentEditableRef.current && description !== contentEditableRef.current.textContent) {
             // TODO: Implement smarter update that preserves cursor position if possible
             // For now, just set the content. This will likely move the cursor to the end.
            contentEditableRef.current.textContent = description;
        }
         // Don't include contentEditableRef in dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [description]);


    const handleInputChangeEmoji = (e) => {
        // Append emoji to the current description state
        // The useEffect above should sync this back to the div
        const updatedDescription = description + e.native;
        if (maxLength === null || updatedDescription.length <= maxLength) {
            setDescription(updatedDescription);
            onDescriptionChange(updatedDescription); // Also notify parent immediately
            setWordCount(updatedDescription.length);
        }
        setIsPickerVisible(false);
         // Focus the div after inserting emoji
        contentEditableRef.current?.focus();
    };

    const handleMention = () => {
        // Append mention symbol
        const updatedDescription = description + '@';
         if (maxLength === null || updatedDescription.length <= maxLength) {
            setDescription(updatedDescription);
            onDescriptionChange(updatedDescription);
            setWordCount(updatedDescription.length);
        }
        // Focus the div
        contentEditableRef.current?.focus();
    };

    const handleThreeDots = async () => {
        try {
            const response = await axios.get(BACKEND_API_URL + "/api/auth/user/templates", {
                params: { workspaceId: activeWorkspace.id },
                headers: { Authorization: `Bearer ${idToken}` }
            });
            setMenuItems(response.data);
        } catch (error) {
            console.error(error);
        }
        setIsMenuOpen(!isMenuOpen);
    };

    const handleMenuItemClick = (id) => {
        const clickedTemplate = menuItems.find((template) => template._id === id);
        if (clickedTemplate) {
            const text = clickedTemplate.info;
            const newDescription = description + ' ' + text; // Add space before template
            if (maxLength === null || newDescription.length <= maxLength) {
                setDescription(newDescription);
                onDescriptionChange(newDescription);
                setWordCount(newDescription.length);
            }
        }
        setIsMenuOpen(false);
        // Focus the div
         contentEditableRef.current?.focus();
    };

    // Close menu/dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuContainerRef.current && !menuContainerRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            if (isVisibilityDropdownOpen && !event.target.closest('.privacy-dropdown')) {
                setIsVisibilityDropdownOpen(false);
            }
            // Close emoji picker on outside click
             if (isPickerVisible && !event.target.closest('.emoji-picker-container') && !event.target.closest('.emoji-button')) {
                 setIsPickerVisible(false);
             }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isVisibilityDropdownOpen, isPickerVisible]); // Added isPickerVisible

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    const handleVisibilityChange = (newVisibility) => {
        setVisibility(newVisibility);
        handlePrivacyStatusChange('privacyStatus', newVisibility);
        setIsVisibilityDropdownOpen(false);
    };

    const getVisibilityIcon = () => {
        switch (visibility) {
            case 'public': return <MdPublic size={18} className="text-blue-500" />;
            case 'private': return <MdLock size={18} />;
            case 'unlisted': return <MdVisibilityOff size={18} className="text-blue-500" />;
            default: return <MdPublic size={18} />;
        }
    };

    const getMaxLengthColor = () => {
        if (maxLength && wordCount >= maxLength) return 'text-red-500';
        if (maxLength && wordCount >= maxLength * 0.8) return 'text-yellow-500';
        return 'text-gray-500';
    };

    const closeModal = () => {
        setIsModalOpenHashtag(false);
        setIsModalOpenCaption(false);
        // Consider refreshing templates if modal could have changed them
        // handleThreeDots();
    };

    // Handler for AI chat modal
    const handleAIResponse = (aiResponse) => {
        // Check if the response is an object with image data (from the updated AIChatModal)
        if (typeof aiResponse === 'object' && aiResponse.hasImage && aiResponse.imageData) {
            // Handle image response
            if (onAIImageGenerated) {
                // Pass the image data to the parent component (UploadForm)
                onAIImageGenerated({
                    firebasePath: aiResponse.imageData.url,
                    thumbnailPath: null, // Images don't need thumbnails
                    videoPath: aiResponse.imageData.filePath || null,
                    hasBeenEdited: true,
                    prompt: aiResponse.imageData.prompt
                });
            }
            
            // Also add the text content if there is any
            if (aiResponse.textContent) {
                const newDescription = description + (description ? ' ' : '') + aiResponse.textContent;
                if (maxLength === null || newDescription.length <= maxLength) {
                    setDescription(newDescription);
                    onDescriptionChange(newDescription);
                    setWordCount(newDescription.length);
                }
            }
        } else {
            // Handle text-only response (existing behavior)
            const aiText = typeof aiResponse === 'string' ? aiResponse : aiResponse.textContent || '';
            const newDescription = description + (description ? ' ' : '') + aiText;
            if (maxLength === null || newDescription.length <= maxLength) {
                setDescription(newDescription);
                onDescriptionChange(newDescription);
                setWordCount(newDescription.length);
            }
        }
        
        setIsAIChatModalOpen(false);
        // Focus the div
        contentEditableRef.current?.focus();
    };

     // Calculate min-height based on rows
    const minHeight = rows * ESTIMATED_LINE_HEIGHT_PX;

    return (
        <div className={`relative w-full border --theme-colors rounded-lg shadow-sm text-sm mb-4 ${isFocused ? 'bg-white' : 'bg-gray-100'}`}>
            {/* Use relative positioning for placeholder AND inline counter */}
            <div className="relative">
                {/* The contentEditable div */}
                <div
                    ref={contentEditableRef}
                    contentEditable={true}
                    // Add padding-right conditionally if hideBottomBar is true
                    className={`w-full p-3 text-sm outline-none resize-none overflow-auto --theme-colors ${isFocused ? 'bg-white' : '.!bg-gray-100'} ${hideBottomBar ? 'pr-16' : ''}`}
                    style={{ minHeight: `${minHeight}px`, whiteSpace: 'pre-wrap' }} // Use minHeight and pre-wrap
                    onInput={handleInput}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    // Suppress default React contentEditable warning if state manages content
                    suppressContentEditableWarning={true}
                    // We might need to manually set textContent in useEffect for initial/external updates
                />
                {/* Placeholder implementation */}
                {!description && !isFocused && (
                    <div className="absolute top-0 left-0 w-full h-full p-3 text-gray-400 pointer-events-none">
                        {placeholder}
                    </div>
                )}

                {/* Inline Character Count (only when hideBottomBar is true) */}
                {hideBottomBar && (
                    <div className={`absolute bottom-1 right-2 text-xs pointer-events-none ${getMaxLengthColor()}`}>
                        {wordCount}
                        {maxLength && <span> / {maxLength}</span>}
                    </div>
                )}
            </div>

            {/* Toolbar Section: Conditionally render the entire bar OR nothing */}
            {!hideBottomBar && (
                <div className="flex items-center gap-2 relative w-full border-t --theme-colors-border p-1">
                    {/* Buttons etc. are shown only when bar is visible */} 
                    {showThreeDots && (
                        <div
                            className="cursor-pointer rounded hover:bg-gray-200 px-2 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-500"
                            onClick={handleThreeDots}
                            title="Insert Template"
                        >
                            <BsThreeDotsVertical size={20} />
                        </div>
                    )}

                    {/* Emoji Button and Picker Container */}
                    <div className="relative emoji-picker-container">
                        <div
                            className="cursor-pointer rounded hover:bg-gray-200 px-2 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-500 emoji-button"
                            onClick={() => setIsPickerVisible(!isPickerVisible)}
                            title="Insert Emoji"
                        >
                            <MdOutlineEmojiEmotions size={20} className="text-yellow-600" />
                        </div>
                        {isPickerVisible && (
                            <div className="absolute bottom-full mb-2 bg-white z-20 shadow-lg rounded"> {/* Position picker above */}
                                <Picker
                                    data={data}
                                    previewPosition="none"
                                    onEmojiSelect={handleInputChangeEmoji}
                                    theme="auto"
                                />
                            </div>
                        )}
                    </div>

                    <div
                        className="cursor-pointer rounded hover:bg-gray-200 px-2 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-500"
                        onClick={handleMention}
                        title="Mention User"
                    >
                        <GoMention size={20} />
                    </div>

                    {/* AI Assistant Button */}
                    <div
                        className="cursor-pointer rounded hover:bg-gray-200 px-2 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-500"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsAIChatModalOpen(true);
                        }}
                        title="AI Assistant"
                    >
                        <FaRobot size={20} className="text-purple-600" />
                    </div>

                    {showPrivacy && (
                        <div className="relative"> {/* Container for dropdown */}
                            <div
                                className="cursor-pointer rounded hover:bg-gray-200 px-2 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-500"
                                onClick={() => setIsVisibilityDropdownOpen(!isVisibilityDropdownOpen)}
                                title="Post Visibility"
                            >
                                <span className="flex items-center gap-1 text-[11px]">{getVisibilityIcon()}</span>
                            </div>
                            {isVisibilityDropdownOpen && (
                                <div className="privacy-dropdown absolute bottom-full mb-2 right-0 --theme-colors z-20 shadow-md rounded-md p-4 space-y-3 min-w-[200px]"> {/* Position dropdown above */}
                                    <div className="text-gray-700 font-medium mb-2">Who can see your post?</div>
                                    <div
                                        className={`flex items-center space-x-3 rounded-lg border p-3 cursor-pointer ${visibility === 'public' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                                        onClick={() => handleVisibilityChange('public')}
                                    >
                                        <MdPublic className="h-5 w-5 text-gray-600" />
                                        <span className={`flex-grow cursor-pointer text-sm text-black ${visibility === 'public' ? '' : 'hover:text-black'}`}>Public</span>
                                    </div>
                                    <div
                                        className={`flex items-center space-x-3 rounded-lg border p-3 cursor-pointer ${visibility === 'private' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                                        onClick={() => handleVisibilityChange('private')}
                                    >
                                        <MdLock className="h-5 w-5 text-gray-600" />
                                        <span className={`flex-grow cursor-pointer text-sm text-black ${visibility === 'private' ? '' : 'hover:text-black'}`}>Private</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Spacer */}
                    <div className="flex-grow cursor-default" />

                    {/* Character Count - Only shown if bottom bar is NOT hidden */}
                    <div className={`text-right text-xs px-3 ${getMaxLengthColor()}`}>
                        {wordCount}
                        {maxLength && <span> / {maxLength}</span>}
                    </div>
                </div> /* End Toolbar Flex Container */
            )}

            {/* Template Menu Container - Stays outside the main toolbar flex container */}
            {isMenuOpen && (
                <div
                    ref={menuContainerRef}
                    className="menu absolute bottom-10 mb-2 left-0 w-[180px] shadow-lg rounded-md z-20 py-2 --theme-colors border" // Position menu above
                >
                    <div className="pt-1 pb-2 px-3 text-sm font-semibold border-b">
                        Insert Template
                    </div>
                    {/* Hashtag Section */}
                    <div className="px-3 pt-2">
                         <div className="flex items-center justify-between gap-2 text-xs font-medium text-gray-500 mb-1">
                            <div className="flex items-center gap-1">
                                <FaHashtag />
                                <span>Hashtag Groups</span>
                            </div>
                            <button
                                className="text-blue-600 hover:text-blue-800 font-bold text-lg leading-none"
                                onClick={() => { setIsMenuOpen(false); setIsModalOpenHashtag(true); }}
                                title="Add Hashtag Group"
                            >
                                +
                            </button>
                        </div>
                        {menuItems?.filter(item => item.template_type === "hashtag").length > 0 ? (
                            menuItems?.filter(item => item.template_type === "hashtag").map(item => (
                                <div
                                    key={item._id}
                                    className="cursor-pointer text-gray-700 hover:text-blue-600 text-sm py-1.5 px-2 rounded hover:bg-gray-100 truncate"
                                    onClick={() => handleMenuItemClick(item._id)}
                                    title={item.name}
                                >
                                    {item.name}
                                </div>
                            ))
                        ) : (
                            <div className="text-gray-400 italic py-1 px-2 text-xs">
                                No hashtag groups
                            </div>
                        )}
                     </div>

                    {/* Caption Section */}
                     <div className="px-3 pt-2 mt-2 border-t">
                         <div className="flex items-center justify-between gap-2 text-xs font-medium text-gray-500 mb-1">
                            <div className="flex items-center gap-1">
                                <FaRegClosedCaptioning />
                                <span>Captions</span>
                            </div>
                             <button
                                className="text-blue-600 hover:text-blue-800 font-bold text-lg leading-none"
                                onClick={() => { setIsMenuOpen(false); setIsModalOpenCaption(true); }}
                                title="Add Caption"
                            >
                                +
                            </button>
                        </div>
                        {menuItems?.filter(item => item.template_type === "caption").length > 0 ? (
                            menuItems?.filter(item => item.template_type === "caption").map(item => (
                                <div
                                    key={item._id}
                                    className="cursor-pointer text-gray-700 hover:text-blue-600 text-sm py-1.5 px-2 rounded hover:bg-gray-100 truncate"
                                    onClick={() => handleMenuItemClick(item._id)}
                                    title={item.name}
                                >
                                    {item.name}
                                </div>
                            ))
                        ) : (
                            <div className="text-gray-400 italic py-1 px-2 text-xs">
                                No captions
                            </div>
                        )}
                     </div>
                </div>
            )}

            {/* Template Modals */}
            {isModalOpenHashtag && (
                <Modal open={isModalOpenHashtag} closeModal={closeModal} type={'TemplatesModal'} otherInfo={'hashtag'} />
            )}
            {isModalOpenCaption && (
                <Modal open={isModalOpenCaption} closeModal={closeModal} type={'TemplatesModal'} otherInfo={'caption'} />
            )}
            
            {/* AI Chat Modal */}
            {isAIChatModalOpen && (
                <AIChatModal
                    isOpen={isAIChatModalOpen}
                    onClose={() => setIsAIChatModalOpen(false)}
                    onConfirm={handleAIResponse}
                    title="AI Assistant"
                />
            )}
        </div>
    );
};

export default Textarea;
